import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import RenderCMSContent from '../RenderCMSContent';
import { getEventLocation, getMapsUrl } from './eventLocations';
import { eventAllowsPuntiPayment } from '../../utils/eventPuntiPayment';
import { CIL_POINTS_LABEL } from '../../utils/cilPoints';

// Distanza verticale tra le sezioni
const SECTION_GAP = { base: 10, md: 14 };

const SectionTitle = ({ children }) => (
  <Heading as="h2" size="md" mt={SECTION_GAP} mb={4} color="gray.800">
    {children}
  </Heading>
);

const formatHHMM = (time) =>
  time ? time.split(':').slice(0, 2).join(':') : null;

const formatDuration = (minutes) => {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h} h ${m} min`;
  return h ? `${h} h` : `${m} min`;
};

export const EventLocationSection = ({ event }) => {
  const location = getEventLocation(event?.Location);
  if (!location) return null;
  const mapsUrl = getMapsUrl(location.address);

  return (
    <>
      <Heading as="h3" size="sm" color="gray.800" mt={SECTION_GAP}>
        {location.title}
        {location.subtitle ? ` · ${location.subtitle}` : ''}
      </Heading>
      {location.address ? (
        <Text color="gray.700" mt={1}>
          {location.address}
        </Text>
      ) : null}
      {location.note ? (
        <Text color="gray.700" mt={1}>
          {location.note}
        </Text>
      ) : null}
      {mapsUrl ? (
        <Button
          as="a"
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          colorScheme="blue"
          size="sm"
          mt={3}
        >
          Apri in Maps
        </Button>
      ) : null}
    </>
  );
};

const ProgramItem = ({ item, isLast }) => {
  const start = formatHHMM(item.orarioInizio);
  const end = formatHHMM(item.orarioFine);
  const duration = formatDuration(item.durataMinuti);
  const color = item.colore || '#6b7280';

  return (
    <Flex gap={3} pb={isLast ? 0 : 5}>
      <Box w={{ base: '52px', md: '62px' }} flex="none" textAlign="right">
        <Text fontWeight="semibold" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {start || '—'}
        </Text>
        {end || duration ? (
          <Text
            fontSize="xs"
            color="gray.500"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {end ? `fino alle ${end}` : duration}
          </Text>
        ) : null}
      </Box>
      <Flex direction="column" align="center" flex="none" w="14px">
        <Box
          w="14px"
          h="14px"
          mt={1}
          borderRadius="full"
          bg={color}
          boxShadow="0 0 0 4px white"
          zIndex={1}
        />
        {!isLast ? <Box flex="1" w="2px" bg="gray.200" mt={1} /> : null}
      </Flex>
      <Box minW={0} flex="1">
        <Flex align="center" gap={2} wrap="wrap">
          <Heading as="h3" size="sm" color="gray.800">
            {item.nome}
          </Heading>
          {item.categoriaLabel ? (
            <Badge
              fontSize="2xs"
              borderRadius="full"
              px={2}
              style={{ color, background: `${color}1f` }}
            >
              {item.categoriaLabel}
            </Badge>
          ) : null}
        </Flex>
        {item.descrizione ? (
          <Text mt={1} color="gray.700" fontSize="sm" whiteSpace="pre-wrap">
            {item.descrizione}
          </Text>
        ) : null}
        <Wrap spacing={2} mt={2}>
          {end && duration ? (
            <WrapItem>
              <Badge fontWeight="normal" textTransform="none">
                Durata {duration}
              </Badge>
            </WrapItem>
          ) : null}
          {item.prenotabile ? (
            <WrapItem>
              <Badge
                fontWeight="normal"
                textTransform="none"
                colorScheme="blue"
              >
                {Number(item.prezzo) === 0 ? 'Gratuito' : `€ ${item.prezzo}`}
              </Badge>
            </WrapItem>
          ) : null}
          {item.capienza ? (
            <WrapItem>
              <Badge fontWeight="normal" textTransform="none">
                Max {item.capienza} persone
              </Badge>
            </WrapItem>
          ) : null}
          {(item.dettagli || []).map((d) => (
            <WrapItem key={d.label}>
              <Badge fontWeight="normal" textTransform="none">
                {d.label}: {d.value}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      </Box>
    </Flex>
  );
};

export const EventProgrammaSection = ({ programma }) => {
  if (!programma?.length) return null;

  return (
    <>
      <SectionTitle>Programma</SectionTitle>
      <Box>
        {programma.map((item, i) => (
          <ProgramItem
            key={item.key}
            item={item}
            isLast={i === programma.length - 1}
          />
        ))}
      </Box>
    </>
  );
};

const euro = (n) => `€ ${Number(n).toLocaleString('it-IT')}`;

const PriceCard = ({ label, value, hint }) => (
  <Box bg="gray.50" borderRadius="md" p={4}>
    <Text
      fontSize="xs"
      letterSpacing="wider"
      textTransform="uppercase"
      color="gray.500"
    >
      {label}
    </Text>
    <Text fontSize="2xl" fontWeight="bold" lineHeight="short" color="gray.800">
      {value}
    </Text>
    {hint ? (
      <Text fontSize="sm" color="gray.600">
        {hint}
      </Text>
    ) : null}
  </Box>
);

export const EventPricingSection = ({ event }) => {
  const hasVariants = event?.Varianti?.length > 0;
  const cards = [];

  if (!hasVariants && !event?.externalURL) {
    if (event?.is_free) {
      cards.push({ label: 'Prezzo', value: 'Gratis' });
    } else if (event?.prezzo != null) {
      cards.push({
        label: 'Prezzo',
        value: euro(event.prezzo),
        hint: event.prezzoOriginale
          ? `invece di ${euro(event.prezzoOriginale)}`
          : null,
      });
    }
  }

  if (event?.paymentStage === 'acconto' && event?.accontoAmount) {
    cards.push({
      label: 'Acconto',
      value: euro(event.accontoAmount),
      hint: 'Il saldo si paga in seguito',
    });
  }

  if (Number(event?.Sconto_Fratelli) > 0) {
    cards.push({
      label: 'Sconto fratelli',
      value: `− ${euro(event.Sconto_Fratelli)}`,
      hint: 'Per ogni fratello iscritto',
    });
  }

  if (eventAllowsPuntiPayment(event)) {
    cards.push({
      label: CIL_POINTS_LABEL,
      value:
        event.pagamentoConPunti === 'solo_punti' ? 'Solo punti' : 'Accettati',
      hint:
        event.pagamentoConPunti === 'solo_punti'
          ? 'Si paga esclusivamente con i punti'
          : 'Puoi pagare anche con i tuoi punti',
    });
  }

  if (!cards.length) return null;

  return (
    <>
      <SectionTitle>Prezzi e condizioni</SectionTitle>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
        {cards.map((c) => (
          <PriceCard key={c.label} {...c} />
        ))}
      </SimpleGrid>
    </>
  );
};

// Strapi salva anche i campi "vuoti" come un paragrafo senza testo: non contano.
const hasBlocks = (content) => {
  if (typeof content === 'string') return content.trim().length > 0;
  if (!Array.isArray(content)) return false;
  return content.some(
    (block) =>
      block?.type === 'image' ||
      (block?.children || []).some(
        (child) => child?.type === 'link' || (child?.text || '').trim()
      )
  );
};

// Con `wide` le liste (spesso di poche parole) si distribuiscono su due colonne,
// così il blocco a tutta larghezza non lascia metà riga vuota.
const LongBlock = ({ content, wide = false }) => (
  <Box
    minW={0}
    sx={{
      // il primo elemento (es. un titolo) non aggiunge margine sopra: allinea con "Dove"
      '& > :first-child': { marginTop: 0 },
      ...(wide && {
        '& ul, & ol': {
          columnCount: { base: 1, md: 2 },
          columnGap: '2.25rem',
        },
        '& li': { breakInside: 'avoid' },
      }),
    }}
  >
    <RenderCMSContent content={content} hideTitle />
  </Box>
);

/**
 * Programma a sinistra e descrizione estesa a destra (stessa riga),
 * descrizione estesa 2 a tutta larghezza, poi 3 e 4 su due colonne.
 * Le parti senza contenuto vengono saltate; sul telefono tutto in colonna.
 */
export const EventProgrammaDettagli = ({ event }) => {
  const hasProgramma = event?.programma?.length > 0;
  const hasLocation = Boolean(getEventLocation(event?.Location));
  const hasLeft = hasProgramma || hasLocation;
  const [l1, l2, l3, l4] = [
    event?.longDescription,
    event?.longDescription2,
    event?.longDescription3,
    event?.longDescription4,
  ];
  const hasLower = hasBlocks(l3) || hasBlocks(l4);

  return (
    <>
      {hasLeft || hasBlocks(l1) ? (
        <SimpleGrid
          columns={{ base: 1, md: hasLeft && hasBlocks(l1) ? 2 : 1 }}
          spacingX={12}
        >
          {hasLeft ? (
            <Box minW={0}>
              <EventLocationSection event={event} />
              <EventProgrammaSection programma={event.programma} />
            </Box>
          ) : null}
          {hasBlocks(l1) ? (
            <Box minW={0} mt={SECTION_GAP}>
              <LongBlock content={l1} />
            </Box>
          ) : null}
        </SimpleGrid>
      ) : null}

      {hasBlocks(l2) ? (
        <Box w="100%" mt={hasBlocks(l1) || hasLeft ? SECTION_GAP : 0}>
          <LongBlock content={l2} wide />
        </Box>
      ) : null}

      {hasLower ? (
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacingX={9}
          spacingY={SECTION_GAP}
          mt={SECTION_GAP}
        >
          {hasBlocks(l3) ? <LongBlock content={l3} /> : null}
          {hasBlocks(l4) ? <LongBlock content={l4} /> : null}
        </SimpleGrid>
      ) : null}
    </>
  );
};
